// ============================================================================
// DEMO TEMPLATE BUILDER PAGE (Admin)
// Create or edit a demonstrator template's metadata, then upload/replace its zip.
// ============================================================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Rocket, AlertCircle, X, Upload, FileArchive, CheckCircle2 } from 'lucide-react';
import {
  getDemoTemplate,
  createDemoTemplate,
  updateDemoTemplate,
  uploadDemoTemplateZip,
  DemoTemplate,
} from '../services/demoTemplatesService';

const CATEGORY_OPTIONS: { value: DemoTemplate['category']; label: string }[] = [
  { value: 'agentic-workflow', label: 'Agentic Workflow' },
  { value: 'customer-portal', label: 'Customer Portal' },
  { value: 'report', label: 'Report' },
  { value: 'other', label: 'Other' },
];

interface ScanFinding {
  file: string;
  pattern: string;
}

export default function DemoTemplateBuilder() {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const isEditMode = !!templateId;

  const [currentTemplateId, setCurrentTemplateId] = useState<string | null>(templateId ?? null);
  const [templateActive, setTemplateActive] = useState(false);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<DemoTemplate['category']>('agentic-workflow');
  const [techStack, setTechStack] = useState('');
  const [requiredEnvVars, setRequiredEnvVars] = useState<string[]>([]);
  const [envVarInput, setEnvVarInput] = useState('');

  const [loading, setLoading] = useState(isEditMode);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadFindings, setUploadFindings] = useState<ScanFinding[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadTemplate = useCallback(async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      const { template } = await getDemoTemplate(id);
      setName(template.name);
      setDescription(template.description);
      setCategory(template.category);
      setTechStack(template.techStack);
      setRequiredEnvVars(template.requiredEnvVars);
      setTemplateActive(template.active);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load template');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (templateId) loadTemplate(templateId);
  }, [templateId, loadTemplate]);

  const addEnvVar = () => {
    const trimmed = envVarInput.trim();
    if (trimmed && !requiredEnvVars.includes(trimmed)) {
      setRequiredEnvVars([...requiredEnvVars, trimmed]);
    }
    setEnvVarInput('');
  };

  const removeEnvVar = (envVar: string) => {
    setRequiredEnvVars(requiredEnvVars.filter((v) => v !== envVar));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !description.trim() || !techStack.trim()) {
      setError('Name, description, and tech stack are required');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      const input = { name: name.trim(), description: description.trim(), category, techStack: techStack.trim(), requiredEnvVars };

      if (currentTemplateId) {
        await updateDemoTemplate(currentTemplateId, input);
      } else {
        const created = await createDemoTemplate(input);
        setCurrentTemplateId(created.templateId);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to save template');
    } finally {
      setSubmitting(false);
    }
  };

  const handleZipFile = async (file: File) => {
    if (!currentTemplateId) return;
    if (!file.name.toLowerCase().endsWith('.zip')) {
      setUploadError('Please upload a .zip file');
      setUploadFindings([]);
      return;
    }

    try {
      setUploading(true);
      setUploadError(null);
      setUploadFindings([]);
      const updated = await uploadDemoTemplateZip(currentTemplateId, file);
      setTemplateActive(updated.active);
    } catch (err: any) {
      setUploadError(err.response?.data?.error || err.message || 'Failed to upload zip');
      setUploadFindings(err.response?.data?.findings || []);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleZipFile(file);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleZipFile(file);
  };

  if (loading) {
    return (
      <div className="flex-1 overflow-y-auto p-8 font-display">
        <div className="w-full max-w-[1400px] mx-auto flex justify-center items-center py-20 text-[#64748b]">
          <div className="text-sm font-medium">Loading template...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">

        <header className="flex items-center justify-between border-b border-slate-200 pb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <Rocket size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-navy">
                {isEditMode ? 'Edit Demonstrator Template' : 'Create Demonstrator Template'}
              </h1>
              <p className="text-slate-500 text-sm font-medium">
                {isEditMode ? 'Update metadata or replace the starter zip' : 'Define metadata, then upload the starter zip'}
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/admin/demo-templates')}
            className="flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors"
          >
            Back to Catalog
          </button>
        </header>

        <form onSubmit={handleSubmit} className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
          {error && (
            <div className="px-5 py-4 border-b border-[#e2e8f0] bg-red-50 flex gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm font-medium text-red-700">{error}</p>
            </div>
          )}

          <div className="p-5 border-b border-[#e2e8f0]">
            <label className="block text-sm font-semibold text-navy mb-2">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Insurance Renewal Agent Starter"
              className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
            />

            <label className="block text-sm font-semibold text-navy mb-2 mt-4">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what this template scaffolds and when to pick it — this is what a skill uses to match a demo type to this template."
              rows={3}
              maxLength={500}
              className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none"
            />
            <p className="mt-1 text-xs text-slate-400 text-right">{description.length}/500</p>

            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Category</label>
                <div className="flex rounded-xl border border-[#e2e8f0] overflow-hidden">
                  {CATEGORY_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setCategory(opt.value)}
                      className={`flex-1 py-2 text-xs font-semibold transition-all ${
                        category === opt.value ? 'bg-primary text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Tech Stack</label>
                <input
                  type="text"
                  value={techStack}
                  onChange={(e) => setTechStack(e.target.value)}
                  placeholder="e.g. nextjs-bedrock"
                  className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                />
              </div>
            </div>
          </div>

          <div className="p-5 border-b border-[#e2e8f0]">
            <label className="block text-sm font-semibold text-navy mb-2">Required Environment Variables</label>
            <p className="text-xs text-slate-400 mb-2">Names only — never store values here.</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={envVarInput}
                onChange={(e) => setEnvVarInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEnvVar(); } }}
                placeholder="e.g. OPENAI_API_KEY"
                className="flex-1 border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none font-mono"
              />
              <button
                type="button"
                onClick={addEnvVar}
                className="rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors"
              >
                Add
              </button>
            </div>
            {requiredEnvVars.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {requiredEnvVars.map((envVar) => (
                  <span
                    key={envVar}
                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-mono bg-slate-100 text-slate-600"
                  >
                    {envVar}
                    <button type="button" onClick={() => removeEnvVar(envVar)} className="text-slate-400 hover:text-red-500">
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end px-5 py-4 bg-slate-50/50">
            <button
              type="submit"
              disabled={submitting}
              className="flex items-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-60"
            >
              {submitting ? 'Saving…' : isEditMode || currentTemplateId ? 'Save Changes' : 'Create Template'}
            </button>
          </div>
        </form>

        {currentTemplateId && (
          <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
            <div className="p-5 flex items-center justify-between border-b border-[#e2e8f0]">
              <div>
                <h2 className="text-sm font-semibold text-navy">Starter Zip</h2>
                <p className="text-xs text-slate-400 mt-0.5">Every upload is scanned for secrets before it's stored.</p>
              </div>
              <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${
                templateActive ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
              }`}>
                {templateActive ? 'Active' : 'No zip uploaded'}
              </span>
            </div>

            <div className="p-5">
              {uploadError && (
                <div className="mb-4 rounded-xl bg-red-50 border border-red-200 p-4">
                  <div className="flex gap-3">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm font-medium text-red-700">{uploadError}</p>
                  </div>
                  {uploadFindings.length > 0 && (
                    <ul className="mt-3 ml-8 flex flex-col gap-1">
                      {uploadFindings.map((f, i) => (
                        <li key={i} className="text-xs text-red-600 font-mono">
                          {f.file} — {f.pattern}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <div
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
                onDrop={handleDrop}
                onClick={() => !uploading && fileInputRef.current?.click()}
                className={`rounded-xl border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${
                  isDragging ? 'border-primary bg-primary/5' : 'border-slate-200 bg-slate-50/50 hover:border-primary/40'
                } ${uploading ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".zip"
                  onChange={handleFileInput}
                  disabled={uploading}
                  className="hidden"
                />
                {uploading ? (
                  <p className="text-sm font-medium text-slate-500">Scanning and uploading…</p>
                ) : templateActive ? (
                  <>
                    <CheckCircle2 className="w-8 h-8 text-primary mx-auto mb-2" />
                    <p className="text-sm font-medium text-navy">Zip uploaded — drop a new one to replace it</p>
                  </>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm font-medium text-navy">Drop the starter repo zip here, or click to browse</p>
                    <p className="text-xs text-slate-400 mt-1 flex items-center justify-center gap-1">
                      <FileArchive size={12} /> .zip only, scanned for secrets on upload
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
